import { CarWheelPosition, CarWheelPositionText } from '@navio/nsdk';

/**
 * The four wheels an inspection records, as the camelCase field name forms, grids and
 * photo galleries key on. Wider positions exist in `CarWheelPositionText` (inner wheels,
 * middle axles); nothing we inspect uses them.
 */
export type WheelField = 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight';

/** One wheel slot for a photo grid: position key, English label, optional image URL. */
export type InspectionWheelPhoto = {
  position: WheelField;
  label: string;
  imageUrl: string | null;
};

export type WheelLocation = (typeof INSPECTION_WHEELS)[number]['location'];

/**
 * One row per wheel: the field name, the API location, and the English label. Every wheel
 * list in the apps derives from this, so a label never reads "Left front" on one screen
 * and "Front left" on the next.
 */
export const INSPECTION_WHEELS = [
  { field: 'frontLeft', location: CarWheelPositionText.FRONT_LEFT, label: 'Front left' },
  { field: 'frontRight', location: CarWheelPositionText.FRONT_RIGHT, label: 'Front right' },
  { field: 'rearLeft', location: CarWheelPositionText.REAR_LEFT, label: 'Rear left' },
  { field: 'rearRight', location: CarWheelPositionText.REAR_RIGHT, label: 'Rear right' }
] as const satisfies readonly { field: WheelField; location: CarWheelPositionText; label: string }[];

export const WHEEL_FIELDS = INSPECTION_WHEELS.map(({ field }) => field) as WheelField[];

export const WHEEL_FIELD_TO_LOCATION = Object.fromEntries(
  INSPECTION_WHEELS.map(({ field, location }) => [field, location])
) as Record<WheelField, WheelLocation>;

export const WHEEL_LABELS = Object.fromEntries(INSPECTION_WHEELS.map(({ field, label }) => [field, label])) as Record<
  WheelField,
  string
>;

const LOCATION_TO_FIELD = Object.fromEntries(
  INSPECTION_WHEELS.map(({ field, location }) => [location, field])
) as Record<WheelLocation, WheelField>;

const isWheelLocation = (location: string | null | undefined): location is WheelLocation =>
  location != null && location in LOCATION_TO_FIELD;

export const wheelFieldFromLocation = (location: string | null | undefined): WheelField | undefined =>
  isWheelLocation(location) ? LOCATION_TO_FIELD[location] : undefined;

/**
 * The numeric `CarWheelPosition` the older wheel endpoints speak, as the text location the
 * inspection endpoints take. Returns undefined for a position we do not inspect.
 */
export const locationFromCarWheelPosition = (position: CarWheelPosition): WheelLocation | undefined => {
  const name = CarWheelPosition[position];
  return isWheelLocation(name) ? name : undefined;
};
